import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Loader2 } from "lucide-react";
import { trackEvent, attributionForSubmission } from "../lib/analytics";
import { isValidPhoneNumber, countryOf } from "../lib/phone";
import PhoneInputE164 from "./PhoneInputE164";

export default function ContactForm({ defaultCity = "", compact = false, preferCountry }) {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", city: defaultCity, subject: "", message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error("Please fill name, email, phone and message.");
            return;
        }
        if (!isValidPhoneNumber(form.phone)) {
            toast.error("Please enter a valid phone number with country code.");
            return;
        }
        setSubmitting(true);
        try {
            const attribution = attributionForSubmission();
            const { data } = await api.post("/contact", { ...form, phone_country: countryOf(form.phone) || null, attribution });
            trackEvent("submit_enquiry", {
                inquiry_id: data?.id,
                form_city: form.city || null,
            });
            toast.success(data.message || "Inquiry sent. We'll be in touch shortly.");
            setForm({ name: "", email: "", phone: "", city: defaultCity, subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className={`bg-white rounded-2xl border border-slate-200 ${compact ? "p-6" : "p-8"} space-y-4`} data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *" testid="contact-name">
                    <input type="text" value={form.name} onChange={update("name")} required data-testid="contact-input-name" className={inputCls} />
                </Field>
                <Field label="Email *" testid="contact-email">
                    <input type="email" value={form.email} onChange={update("email")} required data-testid="contact-input-email" className={inputCls} />
                </Field>
                <Field label="Phone *" testid="contact-phone">
                    <PhoneInputE164 value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required preferCountry={preferCountry} testid="contact-input-phone" />
                </Field>
                <Field label="City" testid="contact-city">
                    <input type="text" value={form.city} onChange={update("city")} data-testid="contact-input-city" className={inputCls} placeholder="Jabalpur / Bhopal / Nagpur / Online" />
                </Field>
            </div>
            <Field label="Subject" testid="contact-subject">
                <input type="text" value={form.subject} onChange={update("subject")} data-testid="contact-input-subject" className={inputCls} placeholder="e.g. NEET preparation for class 12" />
            </Field>
            <Field label="Message *" testid="contact-message">
                <textarea value={form.message} onChange={update("message")} required rows={4} data-testid="contact-input-message" className={`${inputCls} resize-none`} placeholder="Tell us about your child's grade, board, subjects, schedule..." />
            </Field>
            <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="w-full bg-[#1E3A8A] text-white font-semibold py-3 rounded-lg hover:bg-[#1E40AF] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Sending..." : "Send Inquiry"}
            </button>
        </form>
    );
}

const inputCls =
    "w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent transition";

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</span>
        {children}
    </label>
);
